/**
 * Collection of Firestore database collection names and path generators for the barbershop rental system
 * Provides centralized path management for all database collections and subcollections
 * Supports both static collection names and dynamic path generation with optional parameters
 * @constant {object} DB_COLL_NAMES
 * @property {string} APPOINTMENTS - Main appointments collection
 * @property {string} CONTACT_REQUESTS - Contact form submissions collection
 * @property {function} SHOPS - Shop documents and subcollection paths
 * @property {function} SHOPS_BLOCKED_MEMBERS - Blocked members subcollection paths
 * @property {function} SHOPS_BLOCKED_CLIENTS - Blocked clients subcollection paths
 * @property {function} SHOPS_CHAIR - Individual chair document paths
 * @property {function} SHOPS_CHAIRS - Chairs subcollection paths
 * @property {function} SHOPS_CLIENTS - Clients subcollection paths
 * @property {function} SHOPS_CONTACT_REQUESTS - Shop-specific contact requests subcollection paths
 * @property {function} SHOPS_EVENTS - Events subcollection paths
 * @property {function} SHOPS_MEMBERS_REQUESTS - Membership requests subcollection paths
 * @property {function} SHOPS_MEMBER - Individual member document paths
 * @property {function} SHOPS_MEMBERS - Members subcollection paths
 * @property {function} SHOPS_NOTIFICATIONS - Shop notifications subcollection paths
 * @property {function} SHOPS_PAYMENTS - Shop payments subcollection paths
 * @property {function} SHOPS_INVENTORY - Shop inventory subcollection paths
 * @property {function} SHOPS_PRODUCTS - Products subcollection paths
 * @property {function} SHOPS_RATINGS - Shop ratings subcollection paths
 * @property {function} SHOPS_SERVICES - Services subcollection paths
 * @property {function} SHOPS_SETTINGS - Shop settings document paths
 * @property {function} SHOPS_LOCATIONS - Shop locations subcollection paths
 * @property {function} SHOPS_TRANSACTIONS - Shop transactions subcollection paths
 * @property {function} USERS - User documents and subcollection paths
 * @property {function} USERS_FAVORITES - User favorites subcollection paths
 * @property {function} USERS_PREFERENCES - User preferences subcollection paths
 * @property {function} USERS_MEMBERSHIPS - User memberships subcollection paths
 * @property {function} USERS_NOTIFICATIONS - User notifications subcollection paths
 * @property {function} USERS_PAYMENT_METHODS - User payment methods subcollection paths
 * @property {function} USERS_SUBSCRIPTIONS - User subscriptions subcollection paths
 * @property {function} PAYMENTS - Payment transaction documents
 */
export const DB_COLL_NAMES = {
  APP_CITY_STATES: 'appdata_cities_states',
  APP_COUNTRIES: 'appdata_countries',
  APPOINTMENTS: 'appointments',
  CONTACT_REQUESTS: 'contactUsRequests',
  SHOPS: (shopId?: string) => (shopId ? `shops/${shopId}` : 'shops'),
  SHOPS_BLOCKED_MEMBERS: (shopId?: string) =>
    shopId ? `shops/${shopId}/blockedMembers` : 'blockedMembers',
  SHOPS_BLOCKED_CLIENTS: (shopId?: string) =>
    shopId ? `shops/${shopId}/blockedClients` : 'blockedClients',
  SHOPS_CHAIR: (shopId?: string, chairId?: string) =>
    shopId && chairId ? `shops/${shopId}/chairs/${chairId}` : 'chairs',
  SHOPS_CHAIRS: (shopId?: string) => (shopId ? `shops/${shopId}/chairs` : 'chairs'),
  SHOPS_CLIENTS: (shopId?: string) => (shopId ? `shops/${shopId}/clients` : 'clients'),
  SHOPS_CONTACT_REQUESTS: (shopId?: string) =>
    shopId ? `shops/${shopId}/contactRequests` : 'contactRequests',
  SHOPS_EVENTS: (shopId?: string) => (shopId ? `shops/${shopId}/events` : 'events'),
  SHOPS_GALLERY: (shopId?: string) => (shopId ? `shops/${shopId}/gallery` : 'gallery'),
  SHOPS_MEMBERS_REQUESTS: (shopId?: string) =>
    shopId ? `shops/${shopId}/members_requests` : 'members_requests',
  MEMBER: (memberId?: string) => (memberId ? `/members/${memberId}` : 'members'),
  SHOPS_MEMBER_HISTORY: (shopId?: string) =>
    shopId ? `shops/${shopId}/memberHistory` : 'memberHistory',
  SHOPS_NOTIFICATIONS: (shopId?: string) =>
    shopId ? `shops/${shopId}/notifications` : 'notifications',
  SHOPS_QUEUE_ENTRIES: (shopId?: string) =>
    shopId ? `shops/${shopId}/queueEntries` : 'queueEntries',
  SHOPS_PAYMENTS: (shopId?: string) => (shopId ? `shops/${shopId}/payments` : 'payments'),
  USERS_PAYMENTS: (userId?: string) => (userId ? `users/${userId}/payments` : 'payments'),
  SHOPS_INVENTORY: (shopId?: string) => (shopId ? `shops/${shopId}/inventory` : 'inventory'),
  SHOPS_PRODUCTS: (shopId?: string) => (shopId ? `shops/${shopId}/products` : 'products'),
  SHOPS_PRODUCTS_RATINGS: (shopId?: string, productId?: string) =>
    shopId && productId ? `shops/${shopId}/products/${productId}/ratings` : 'products_ratings',
  SHOPS_STAFF_RATINGS: (shopId?: string, staffId?: string) =>
    shopId && staffId ? `shops/${shopId}/staff/${staffId}/ratings` : 'staff_ratings',
  SHOPS_RATINGS: (shopId?: string) => (shopId ? `shops/${shopId}/ratings` : 'ratings'),
  SHOPS_SERVICES: (shopId?: string) => (shopId ? `shops/${shopId}/services` : 'services'),
  SHOPS_SERVICE_CATEGORIES: (shopId?: string) =>
    shopId ? `shops/${shopId}/serviceCategories` : 'serviceCategories',
  SHOPS_MEMBER_SERVICES: (shopId?: string) =>
    shopId ? `shops/${shopId}/memberServices` : 'memberServices',
  SHOPS_MEMBER_TIME_OFF: (shopId?: string) =>
    shopId ? `shops/${shopId}/memberTimeOff` : 'memberTimeOff',
  SHOPS_SLOT_LOCKS: (shopId?: string) => (shopId ? `shops/${shopId}/slotLocks` : 'slotLocks'),
  SHOPS_SCHEDULING_GUARDS: (shopId?: string) =>
    shopId ? `shops/${shopId}/schedulingGuards` : 'schedulingGuards',
  SHOPS_SETTINGS: (shopId?: string) => (shopId ? `shops/${shopId}/settings` : 'settings'),
  SHOPS_CLIENT_SETTINGS: (shopId?: string) =>
    shopId ? `shops/${shopId}/settings/client` : 'settings/client',
  SHOPS_SCHEDULE_EXCEPTIONS: (shopId?: string) =>
    shopId ? `shops/${shopId}/scheduleExceptions` : 'scheduleExceptions',
  SHOPS_LOCATIONS: (shopId?: string) => (shopId ? `shops/${shopId}/locations` : 'locations'),
  SHOPS_TRANSACTIONS: (shopId?: string) =>
    shopId ? `shops/${shopId}/transactions` : 'transactions',
  USERS: (userId?: string) => (userId ? `users/${userId}` : 'users'),
  USERS_FAVORITES: (userId?: string) => (userId ? `users/${userId}/favorites` : 'favorites'),
  USERS_PREFERENCES: (userId?: string) => (userId ? `users/${userId}/preferences` : 'preferences'),
  USERS_NOTIFICATION_PREFERENCES: (userId?: string) =>
    userId ? `users/${userId}/notificationPreferences` : 'notificationPreferences',
  USERS_MEMBERSHIPS: (userId?: string) => (userId ? `users/${userId}/memberships` : 'memberships'),
  USERS_NOTIFICATIONS: (userId?: string) =>
    userId ? `users/${userId}/notifications` : 'notifications',
  USERS_PAYMENT_METHODS: (userId?: string) =>
    userId ? `users/${userId}/paymentMethods` : 'paymentMethods',
  USERS_SUBSCRIPTIONS: (userId?: string) =>
    userId ? `users/${userId}/subscriptions` : 'subscriptions',
  PAYMENTS: (paymentId?: string) => (paymentId ? `payments/${paymentId}` : 'payments'),
  STATS_SHOPS_COUNTRY: 'stats_shops_country',
  STATS_SHOPS_STATE: 'stats_shops_state',
  STATS_SHOPS_CITY: 'stats_shops_city',
};

/**
 * Type representing all available database collection keys
 * @type {DBCollectionKey}
 */
type DBCollectionKey = keyof typeof DB_COLL_NAMES;

/**
 * Conditional type that extracts the return type of collection path functions
 * For function properties, returns the function's return type; for string properties, returns the string type
 * @type {DBCollectionPath}
 * @template K - The collection key to resolve the path type for
 */
export type DBCollectionPath<K extends DBCollectionKey> = (typeof DB_COLL_NAMES)[K] extends (
  ...args: any[]
) => infer R
  ? R
  : (typeof DB_COLL_NAMES)[K];

/**
 * Collection of backup Firestore database collection names and path generators
 * Used for data archival and recovery operations in the barbershop rental system
 * Maintains separate backup collections for critical business data
 * @constant {object} BACKUP_COLL_NAMES
 * @property {string} BACKUP_USERS - Backup collection for user data
 * @property {function} BACKUP_USERS_PREFERENCES - Backup paths for user preference data
 * @property {string} BACKUP_SHOPS - Backup collection for shop data
 * @property {function} BACKUP_SHOPS_SERVICES - Backup paths for shop service data
 * @property {function} BACKUP_SHOPS_RATINGS - Backup paths for shop rating data
 * @property {function} BACKUP_SHOPS_APPOINTMENTS - Backup paths for appointment data
 */
export const BACKUP_COLL_NAMES = {
  BACKUP_USERS: 'users_backup',
  BACKUP_USERS_PREFERENCES: (userId: string) =>
    `${BACKUP_COLL_NAMES.BACKUP_USERS}/${userId}/preferences`,
  BACKUP_USERS_MEMBERSHIPS: (userId: string) =>
    `${BACKUP_COLL_NAMES.BACKUP_USERS}/${userId}/memberships`,
  BACKUP_SHOPS: 'shops_backup',
  BACKUP_SHOPS_SERVICES: (shopId?: string) =>
    `${BACKUP_COLL_NAMES.BACKUP_SHOPS}/${shopId}/services`,
  BACKUP_SHOPS_RATINGS: (shopId?: string) => `${BACKUP_COLL_NAMES.BACKUP_SHOPS}/${shopId}/ratings`,
  BACKUP_SHOPS_APPOINTMENTS: (shopId?: string) =>
    `${BACKUP_COLL_NAMES.BACKUP_SHOPS}/${shopId}/appointments`,
};

/**
 * Type representing all available backup collection keys
 * @type {BackupCollectionKey}
 */
type BackupCollectionKey = keyof typeof BACKUP_COLL_NAMES;

/**
 * Conditional type that extracts the return type of backup collection path functions
 * For function properties, returns the function's return type; for string properties, returns the string type
 * @type {BackupCollectionPath}
 * @template K - The backup collection key to resolve the path type for
 */
export type BackupCollectionPath<K extends BackupCollectionKey> =
  (typeof BACKUP_COLL_NAMES)[K] extends (...args: any[]) => infer R
    ? R
    : (typeof BACKUP_COLL_NAMES)[K];
